package org.zq.astrock_ai.ui.components

import org.zq.astrock_ai.theme.LocalAppFontFamily

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.zq.astrock_ai.data.IndexQuote
import org.zq.astrock_ai.data.MarketOverview
import org.zq.astrock_ai.network.MarketCenterData
import org.zq.astrock_ai.data.RankedStock
import org.zq.astrock_ai.data.SectorQuote
import org.zq.astrock_ai.theme.BinanceColors

@Composable
fun MarketCenterPage(
    onStockSelect: (String) -> Unit,
    compact: Boolean,
    marketData: MarketCenterData,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(if (compact) 12.dp else 16.dp)) {
        MarketOverviewPanel(marketData.overview, compact)
        IndexGridPanel(marketData.indices, compact)
        SectorPanel(marketData.sectors, compact)
        RankPanel(
            onStockSelect = onStockSelect,
            compact = compact,
            gainers = marketData.gainers,
            losers = marketData.losers,
            turnoverLeaders = marketData.turnoverLeaders,
        )
    }
}

@Composable
private fun MarketOverviewPanel(overview: MarketOverview, compact: Boolean) {
    GlassPanel(modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(if (compact) 14.dp else 20.dp)) {
            Text("市场概览", fontWeight = FontWeight.SemiBold, fontSize = if (compact) 16.sp else 17.sp)
            Spacer(Modifier.height(12.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OverviewStat("上涨", "${overview.upCount}", BinanceColors.Green, Modifier.weight(1f))
                OverviewStat("下跌", "${overview.downCount}", BinanceColors.Red, Modifier.weight(1f))
                OverviewStat("平盘", "${overview.flatCount}", BinanceColors.Muted, Modifier.weight(1f))
            }
            Spacer(Modifier.height(12.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                OverviewLabelValue("两市成交额", overview.turnover)
                OverviewLabelValue("北向资金", overview.northboundFlow, BinanceColors.Green)
            }
        }
    }
}

@Composable
private fun OverviewStat(label: String, value: String, color: androidx.compose.ui.graphics.Color, modifier: Modifier) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(BinanceColors.Elevated)
            .padding(vertical = 10.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(label, fontSize = 12.sp, color = BinanceColors.Muted)
        Spacer(Modifier.height(4.dp))
        Text(value, fontFamily = LocalAppFontFamily.current, fontSize = 16.sp, fontWeight = FontWeight.Bold, color = color)
    }
}

@Composable
private fun OverviewLabelValue(label: String, value: String, valueColor: androidx.compose.ui.graphics.Color = BinanceColors.Body) {
    Column {
        Text(label, fontSize = 12.sp, color = BinanceColors.Muted)
        Text(value, fontFamily = LocalAppFontFamily.current, fontSize = 15.sp, fontWeight = FontWeight.SemiBold, color = valueColor)
    }
}

@Composable
private fun IndexGridPanel(indices: List<IndexQuote>, compact: Boolean) {
    GlassPanel(modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(if (compact) 14.dp else 20.dp)) {
            Text("主要指数", fontWeight = FontWeight.SemiBold, fontSize = if (compact) 16.sp else 17.sp)
            Spacer(Modifier.height(12.dp))
            val columns = if (compact) 2 else 4
            indices.chunked(columns).forEach { row ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    row.forEach { index ->
                        IndexCard(index, Modifier.weight(1f))
                    }
                    repeat(columns - row.size) {
                        Spacer(Modifier.weight(1f))
                    }
                }
                Spacer(Modifier.height(8.dp))
            }
        }
    }
}

@Composable
private fun IndexCard(quote: IndexQuote, modifier: Modifier) {
    val color = if (quote.isUp) BinanceColors.Green else BinanceColors.Red
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(BinanceColors.Elevated)
            .padding(horizontal = 10.dp, vertical = 10.dp),
    ) {
        Text(quote.name, fontSize = 12.sp, color = BinanceColors.Muted, maxLines = 1)
        Spacer(Modifier.height(4.dp))
        Text(quote.price, fontFamily = LocalAppFontFamily.current, fontSize = 14.sp, fontWeight = FontWeight.SemiBold, color = BinanceColors.Body)
        Text(quote.changePercent, fontFamily = LocalAppFontFamily.current, fontSize = 12.sp, color = color)
    }
}

@Composable
private fun SectorPanel(sectors: List<SectorQuote>, compact: Boolean) {
    GlassPanel(modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(if (compact) 14.dp else 20.dp)) {
            Text("行业板块", fontWeight = FontWeight.SemiBold, fontSize = if (compact) 16.sp else 17.sp)
            Spacer(Modifier.height(12.dp))
            if (compact) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    sectors.forEach { SectorRow(it) }
                }
            } else {
                Row(
                    modifier = Modifier.horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    sectors.forEach { sector ->
                        SectorChip(sector)
                    }
                }
            }
        }
    }
}

@Composable
private fun SectorChip(sector: SectorQuote) {
    val color = if (sector.isUp) BinanceColors.Green else BinanceColors.Red
    Column(
        modifier = Modifier
            .width(120.dp)
            .clip(RoundedCornerShape(8.dp))
            .background(BinanceColors.Elevated)
            .padding(12.dp),
    ) {
        Text(sector.name, fontSize = 13.sp, fontWeight = FontWeight.Medium)
        Spacer(Modifier.height(4.dp))
        Text(sector.changePercent, fontFamily = LocalAppFontFamily.current, fontSize = 14.sp, fontWeight = FontWeight.Bold, color = color)
        Text("领涨 ${sector.leadingStock}", fontSize = 11.sp, color = BinanceColors.Muted)
    }
}

@Composable
private fun SectorRow(sector: SectorQuote) {
    val color = if (sector.isUp) BinanceColors.Green else BinanceColors.Red
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(BinanceColors.Elevated)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column {
            Text(sector.name, fontSize = 14.sp, fontWeight = FontWeight.Medium)
            Text("领涨 ${sector.leadingStock}", fontSize = 11.sp, color = BinanceColors.Muted)
        }
        Text(sector.changePercent, fontFamily = LocalAppFontFamily.current, fontSize = 14.sp, fontWeight = FontWeight.Bold, color = color)
    }
}

@Composable
private fun RankPanel(
    onStockSelect: (String) -> Unit,
    compact: Boolean,
    gainers: List<RankedStock>,
    losers: List<RankedStock>,
    turnoverLeaders: List<RankedStock>,
) {
    var tabIndex by remember { mutableIntStateOf(0) }
    val tabs = listOf("涨幅榜", "跌幅榜", "成交额")
    val stocks = when (tabIndex) {
        0 -> gainers
        1 -> losers
        else -> turnoverLeaders
    }

    GlassPanel(modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(if (compact) 14.dp else 20.dp)) {
            Text("个股排行", fontWeight = FontWeight.SemiBold, fontSize = if (compact) 16.sp else 17.sp)
            Spacer(Modifier.height(12.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(8.dp))
                    .background(BinanceColors.Elevated)
                    .padding(4.dp),
            ) {
                tabs.forEachIndexed { index, tab ->
                    Text(
                        tab,
                        fontSize = 13.sp,
                        fontWeight = if (index == tabIndex) FontWeight.SemiBold else FontWeight.Normal,
                        color = if (index == tabIndex) BinanceColors.DarkText else BinanceColors.Muted,
                        textAlign = TextAlign.Center,
                        modifier = Modifier
                            .weight(1f)
                            .clip(RoundedCornerShape(6.dp))
                            .background(if (index == tabIndex) BinanceColors.Yellow else androidx.compose.ui.graphics.Color.Transparent)
                            .clickable { tabIndex = index }
                            .padding(vertical = 8.dp),
                    )
                }
            }
            Spacer(Modifier.height(8.dp))
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp, vertical = 6.dp),
            ) {
                Text("#", fontSize = 11.sp, color = BinanceColors.Muted, modifier = Modifier.width(24.dp))
                Text("名称", fontSize = 11.sp, color = BinanceColors.Muted, modifier = Modifier.weight(1f))
                Text("最新价", fontSize = 11.sp, color = BinanceColors.Muted, modifier = Modifier.width(72.dp), textAlign = TextAlign.End)
                Text(if (tabIndex == 2) "成交额" else "涨跌幅", fontSize = 11.sp, color = BinanceColors.Muted, modifier = Modifier.width(64.dp), textAlign = TextAlign.End)
            }
            HorizontalDivider(color = BinanceColors.Hairline)
            stocks.forEach { stock ->
                RankedStockRow(stock, showTurnover = tabIndex == 2, onClick = { onStockSelect(stock.code) })
                HorizontalDivider(color = BinanceColors.Hairline.copy(alpha = 0.5f))
            }
        }
    }
}

@Composable
private fun RankedStockRow(stock: RankedStock, showTurnover: Boolean, onClick: () -> Unit) {
    val color = if (stock.isUp) BinanceColors.Green else BinanceColors.Red
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 4.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            "${stock.rank}",
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
            color = if (stock.rank <= 3) BinanceColors.Yellow else BinanceColors.Muted,
            modifier = Modifier.width(24.dp),
        )
        Column(Modifier.weight(1f)) {
            Text(stock.name, fontSize = 14.sp, fontWeight = FontWeight.Medium)
            Text(stock.code, fontSize = 11.sp, color = BinanceColors.Muted)
        }
        Text(stock.price, fontFamily = LocalAppFontFamily.current, fontSize = 13.sp, modifier = Modifier.width(72.dp), textAlign = TextAlign.End)
        Text(
            if (showTurnover) stock.turnover else stock.changePercent,
            fontFamily = LocalAppFontFamily.current,
            fontSize = 13.sp,
            color = if (showTurnover) BinanceColors.Body else color,
            modifier = Modifier.width(64.dp),
            textAlign = TextAlign.End,
        )
    }
}
